type StorageKind = 'localStorage' | 'sessionStorage';

/**
 * Web Storage that never throws.
 *
 * Browsers can block storage entirely (strict privacy settings, some in-app browsers,
 * full quota). Merely touching `window.localStorage` then throws a SecurityError, which
 * used to blank the whole site. This wrapper degrades to "nothing is remembered".
 */
function createSafeStorage(kind: StorageKind) {
  const storage = (): Storage | null => {
    try {
      return window[kind];
    } catch {
      return null;
    }
  };

  return {
    getItem(key: string): string | null {
      try {
        return storage()?.getItem(key) ?? null;
      } catch {
        return null;
      }
    },
    /** Returns whether the value was actually stored. */
    setItem(key: string, value: string): boolean {
      try {
        const target = storage();
        if (!target) return false;
        target.setItem(key, value);
        return true;
      } catch {
        return false;
      }
    },
    removeItem(key: string): void {
      try {
        storage()?.removeItem(key);
      } catch {
        // Nothing to clean up if storage is unavailable.
      }
    },
  };
}

export const safeLocalStorage = createSafeStorage('localStorage');
export const safeSessionStorage = createSafeStorage('sessionStorage');
